import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useSentinelChat } from './SentinelChatProvider'
import Guide from './Guide'
import AssistantMessage from './AssistantMessage'

/**
 * Single-column chat transcript. Renders the ENTIRE conversation: each user
 * question as a right-aligned bubble, each assistant turn via AssistantMessage
 * (results inline). Owns the scroll container, auto-scrolls to the latest turn
 * while the reader is pinned to the bottom, and shows a "more below" affordance
 * when there is content past the fold.
 */
export default function Transcript() {
  const { t } = useTranslation()
  const { turns } = useSentinelChat()
  const scrollRef = useRef(null)
  const bottomRef = useRef(null)
  const stick = useRef(true)
  const [moreBelow, setMoreBelow] = useState(false)

  const lastAssistant = [...turns].reverse().find((t) => t.role === 'assistant')
  const lastStatus = lastAssistant ? lastAssistant.status : null

  const recompute = () => {
    const el = scrollRef.current
    if (!el) return
    const distance = el.scrollHeight - el.scrollTop - el.clientHeight
    stick.current = distance < 120
    setMoreBelow(distance > 40)
  }

  // Auto-scroll to the newest turn when one arrives or resolves — but only if
  // the reader is already near the bottom — then refresh the affordance.
  useEffect(() => {
    if (stick.current && bottomRef.current) {
      bottomRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' })
    }
    const id = requestAnimationFrame(recompute)
    return () => cancelAnimationFrame(id)
  }, [turns.length, lastStatus])

  const scrollToBottom = () => {
    stick.current = true
    setMoreBelow(false)
    if (bottomRef.current) bottomRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' })
  }

  return (
    <div className="fd-chat__scrollwrap">
      <div className="fd-chat__scroll" ref={scrollRef} onScroll={recompute}>
        <div className="fd-transcript">
          {turns.length === 0 ? (
            <Guide />
          ) : (
            turns.map((turn) =>
              turn.role === 'user' ? (
                <div key={turn.id} className="fd-msg fd-msg--user">
                  <div className="fd-msg__bubble">{turn.question}</div>
                </div>
              ) : (
                <AssistantMessage key={turn.id} turn={turn} />
              ),
            )
          )}
          <div ref={bottomRef} />
        </div>
      </div>

      {moreBelow && (
        <button className="fd-scroll-down" aria-label={t('Sentinel.V2.scrollToLatest')} onClick={scrollToBottom}>
          <i className="fa-solid fa-chevron-down" aria-hidden="true" />
        </button>
      )}
    </div>
  )
}
